import type { KpiTrends, SeriesPoint, TrendCompare } from "@/lib/dashApi";
import { fmtPct, fmtValue } from "@/lib/format";
import { EmptyState } from "@/components/dash/ui";

/** Lightweight dependency-free SVG line/area chart. */
export function LineChart({
  points,
  unit = "count",
  height = 160,
}: {
  points: SeriesPoint[];
  unit?: string;
  height?: number;
}) {
  if (points.length < 2) return <EmptyState compact>Not enough history to chart yet.</EmptyState>;
  const W = 600;
  const H = height;
  const pad = 8;
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const dx = (W - pad * 2) / (points.length - 1);
  const x = (i: number) => pad + i * dx;
  const y = (v: number) => H - pad - ((v - min) / span) * (H - pad * 2);
  const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(" ");
  const area = `${line} L${x(points.length - 1).toFixed(1)},${H - pad} L${x(0).toFixed(1)},${H - pad} Z`;
  const last = points[points.length - 1];
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-2xl font-semibold tabular-nums text-white">{fmtValue(last.value, unit)}</span>
        <span className="text-xs text-slate-500">{points.length} days · {points[0].date} → {last.date}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-40 w-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="lcfill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgb(99,102,241)" stopOpacity="0.35" />
            <stop offset="100%" stopColor="rgb(99,102,241)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#lcfill)" />
        <path d={line} fill="none" stroke="rgb(129,140,248)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="mt-1 flex justify-between text-[10px] text-slate-600">
        <span>min {fmtValue(min, unit)}</span>
        <span>max {fmtValue(max, unit)}</span>
      </div>
    </div>
  );
}

function TrendCell({ label, c, unit }: { label: string; c: TrendCompare; unit: string }) {
  const pct = c.change_pct;
  const up = (pct ?? 0) >= 0;
  return (
    <div className="rounded-lg border border-ink-500/50 bg-ink-700/40 px-3 py-2.5">
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 text-lg font-semibold tabular-nums text-white">
        {c.current === null ? "—" : fmtValue(c.current, unit)}
      </p>
      <p className={`text-[11px] ${pct === null ? "text-slate-600" : up ? "text-emerald-400" : "text-rose-400"}`}>
        {pct === null ? "no prior data" : `${up ? "▲" : "▼"} ${fmtPct(pct)}`}
      </p>
    </div>
  );
}

export function TrendQuad({ trends }: { trends: KpiTrends }) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <TrendCell label="Week on week" c={trends.wow} unit={trends.unit} />
      <TrendCell label="Month on month" c={trends.mom} unit={trends.unit} />
      <TrendCell label="Quarter on quarter" c={trends.qoq} unit={trends.unit} />
      <TrendCell label="Year on year" c={trends.yoy} unit={trends.unit} />
    </div>
  );
}
